import type { SessionSummary } from './types';

// 本番でも使う、セッション一覧・タイトル表示まわりの UI ロジック（API 実装には依存しない）。
// モック専用のアシスタント応答生成は `mockReply.ts` に隔離している。

const ONE_DAY_MS = 24 * 60 * 60 * 1000;
export type SessionGroupKey = 'today' | 'past7Days' | 'past30Days' | 'older';
export interface SessionGroup {
  key: SessionGroupKey;
  sessions: SessionSummary[];
}

/**
 * セッションの preview を表示用に整形する。空文字 / undefined はフォールバック文言に置換する。
 * 「undefined」と表示されるのを撲滅するための共通ガード。
 */
export function getSessionDisplayTitle(session: Pick<SessionSummary, 'preview'> | null | undefined, fallback: string): string {
  const preview = session?.preview?.trim();
  return preview && preview.length > 0 ? preview : fallback;
}

/**
 * セッションを `lastMessageAt` の新しさで 4 グループに振り分ける。
 * 空のグループは含めない。元の sort 順（lastMessageAt 降順）は維持する。
 */
export function groupSessionsByRecency(sessions: SessionSummary[], now: Date = new Date()): SessionGroup[] {
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const buckets: Record<SessionGroupKey, SessionSummary[]> = {
    today: [],
    past7Days: [],
    past30Days: [],
    older: []
  };
  for (const session of sessions) {
    const time = new Date(session.lastMessageAt).getTime();
    if (Number.isNaN(time)) {
      buckets.older.push(session);
      continue;
    }
    if (time >= startOfToday) {
      buckets.today.push(session);
    } else if (time >= startOfToday - 7 * ONE_DAY_MS) {
      buckets.past7Days.push(session);
    } else if (time >= startOfToday - 30 * ONE_DAY_MS) {
      buckets.past30Days.push(session);
    } else {
      buckets.older.push(session);
    }
  }
  return [{
    key: 'today' as const,
    sessions: buckets.today
  }, {
    key: 'past7Days' as const,
    sessions: buckets.past7Days
  }, {
    key: 'past30Days' as const,
    sessions: buckets.past30Days
  }, {
    key: 'older' as const,
    sessions: buckets.older
  }].filter(group => group.sessions.length > 0);
}